<template>
  <div class="azcon-leaderboard-card">
    <div v-if="loading" class="azcon-leaderboard-card__skeleton-h2"></div>
    <h2 v-else-if="title">{{ title }}</h2>
    <leaderboard-card
      :current_user="current_user"
      :dashboard_item="dashboard_item"
      :leaderTableVisible="leaderTableVisible"
      :leader_board="leader_board"
    />
  </div>
</template>

<script>
import LeaderboardCard from "@/view/components/card/LeaderboardCard.vue";

export default {
  name: "AzconLeaderboardCard",
  components: { LeaderboardCard },
  props: {
    leaderTableVisible: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    current_user: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    leader_board: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    dashboard_item: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
};
</script>

<style lang="scss" scoped>
.azcon-leaderboard-card {
  h2 {
    margin: 0;
    font-size: 20px;
  }

  &__skeleton-h2 {
    width: 140px;
    height: 20px;
    margin: 0;
    border-radius: 6px;
    background: linear-gradient(90deg, #dce3eb 25%, #d0d8e2 50%, #dce3eb 75%);
    background-size: 200% 100%;
    animation: azcon-lb-shimmer 1.5s infinite;
  }

  /* ── Desktop card ─────────────────────────────────────────────────── */
  ::v-deep .leaderboard-desktop {
    background: transparent;
    border-radius: 0 !important;
    border: none;
    box-shadow: none;

    .course-card-body {
      padding: 0;
    }
  }

  /* ── Desktop list items ───────────────────────────────────────────── */
  ::v-deep #leadership {
    li {
      border-radius: 10px;
      padding: 10px;
      transition: background 0.18s ease;

      &:hover {
        background: #f8fafc;
      }

      .line {
        color: #64748b;
      }

      .points-value {
        color: #2563eb;
        font-weight: 700;
      }
    }

    li.yourself {
      background: #eff6ff;
      border: 1px solid #bfdbfe;
      border-radius: 10px;
      padding: 12px 10px;

      .line {
        color: #2563eb;
        font-weight: 600;
      }

      .points-value {
        color: #2563eb;
        font-weight: 800;
      }
    }

    li.active {
      border-left: 3px solid #2563eb !important;
      background: #f0f5ff !important;
      border-radius: 10px;

      .line {
        color: #2563eb;
      }

      .user-name {
        color: #0f172a;
        font-weight: 600;
      }

      .points-value {
        color: #2563eb;
        font-weight: 800;
      }
    }
  }

  /* ── Desktop skeleton ─────────────────────────────────────────────── */
  ::v-deep .skeleton-leaderboard {
    .skeleton-leaderboard-item {
      border-color: #e8edf3;

      &.yourself {
        background: #eff6ff;
        border-radius: 10px;
      }
    }

    .skeleton-avatar,
    .skeleton-text,
    .skeleton-points,
    .skeleton-rank {
      background: linear-gradient(90deg, #e8edf3 25%, #dce3eb 50%, #e8edf3 75%);
      background-size: 200% 100%;
    }
  }

  /* ── Mobile card ──────────────────────────────────────────────────── */
  ::v-deep .leaderboard-mobile-card {
    background: transparent;
    border-radius: 0 !important;
    border: none;
    box-shadow: none;

    .leaderboard-mobile-icon {
      background: #eff6ff;
      border-radius: 10px;

      i {
        color: #2563eb;
      }
    }

    .leaderboard-mobile-title {
      font-family: inherit;
      font-size: 14px;
      font-weight: 600;
      color: #333;
    }

    .leaderboard-mobile-rank {
      color: #64748b;
      font-weight: 600;
    }

    .leaderboard-mobile-rank-skeleton {
      background: linear-gradient(90deg, #e8edf3 25%, #dce3eb 50%, #e8edf3 75%);
      background-size: 200% 100%;
    }

    .lb-toggle-btn {
      background: #fff !important;
      border: 1px solid #d5dde6 !important;
      border-radius: 10px;
      color: #2563eb !important;
      font-weight: 600;

      i {
        color: #2563eb !important;
      }

      &--active {
        background: #eff6ff !important;
        border-color: #bfdbfe !important;
      }
    }

    .leaderboard-mobile-content {
      border-top-color: #d5dde6;
    }
  }

  /* ── Mobile list items ────────────────────────────────────────────── */
  ::v-deep #leadership-mobile {
    li {
      border-radius: 10px;
      transition: background 0.18s ease;

      .line {
        color: #64748b;
      }

      .points-value {
        color: #2563eb;
        font-weight: 700;
      }
    }

    li.active {
      background: #f0f5ff !important;
      border-left: 3px solid #2563eb !important;
      border-radius: 10px;

      .line {
        color: #2563eb;
      }

      .points-value {
        color: #2563eb;
        font-weight: 800;
      }
    }
  }

  /* ── Mobile skeleton ──────────────────────────────────────────────── */
  ::v-deep .skeleton-leaderboard-mobile {
    .skeleton-mobile-rank,
    .skeleton-mobile-avatar,
    .skeleton-mobile-name,
    .skeleton-mobile-points {
      background: linear-gradient(90deg, #e8edf3 25%, #dce3eb 50%, #e8edf3 75%);
      background-size: 200% 100%;
    }

    .skeleton-mobile-item {
      border-color: #e8edf3;
    }
  }

  @media (max-width: 768px) {
    ::v-deep .leaderboard-mobile-card {
      margin-top: 0;

      .leaderboard-mobile-header {
        min-height: 72px;
        padding: 12px 16px;
      }

      .leaderboard-mobile-info {
        min-width: 0;
      }

      .leaderboard-mobile-title,
      .leaderboard-mobile-rank {
        font-size: 14px;
        line-height: 1.4;
      }

      .lb-toggle-btn {
        flex: 0 0 auto;
        min-width: 48px;
        min-height: 48px;
        max-width: 46%;
        padding: 12px 16px;
        font-size: 14px;
        line-height: 1.3;

        span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }

    ::v-deep #leadership-mobile {
      li,
      li.active {
        min-height: 56px;
        padding-top: 12px;
        padding-bottom: 12px;
        font-size: 14px;
      }

      li .points-value {
        margin-left: 8px;
        font-size: 14px;
        line-height: 1.4;
      }

      li .text-truncate {
        font-size: 14px;
        line-height: 1.4;
      }
    }
  }
}

@keyframes azcon-lb-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
